'use client'

import dynamic from 'next/dynamic'
import Image from 'next/image'
import { alpha, useTheme } from '@mui/material/styles'
import { Box, Typography } from '@mui/material'

// Types
import type { PainSlideProps } from '@/types/components/molecules/home'

// Components
const MenuItemIconAtom = dynamic(() => import('@/components/atoms/icon/menu-item'))
const CrossIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/cross'))

// Variables
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'

// CSS enter transition for a part of the active slide; it leaves at once and comes back after the given delay.
// Movement goes in inline style so the RTL style plugin does not mirror it
const enter = (active: boolean, delay: number, duration: number, hidden: { translate?: string; scale?: string }) => ({
	sx: {
		opacity: active ? 1 : 0,
		transition: `opacity ${duration}s ${EASE} ${active ? delay : 0}s, translate ${duration}s ${EASE} ${active ? delay : 0}s, scale ${duration}s ${EASE} ${active ? delay : 0}s`,
		'@media (prefers-reduced-motion: reduce)': { transition: 'none' }
	},
	style: active ? { translate: '0 0', scale: '1' } : hidden
})

// Full-width photo slide with the group title and its pains over a dark gradient
const PainSlideMolecule = (props: PainSlideProps) => {
	// Props
	const { icon, title, items, image, active } = props

	// Variables
	const theme = useTheme()
	const dark = theme.palette.primary.ultradark ?? theme.palette.primary.dark

	return (
		<Box sx={{ position: 'relative', overflow: 'hidden', height: 1, minHeight: { xs: 560, md: 560 }, borderRadius: { xs: 5, md: 8 }, bgcolor: 'primary.ultradark' }}>
			{/* Slow zoom out while the slide is showing */}
			<Box
				sx={{ position: 'absolute', inset: 0, transition: `scale ${active ? 8 : 0.6}s ease-out`, '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}
				style={{ scale: active ? '1' : '1.12' }}
			>
				<Image src={image} alt={title} fill sizes="(max-width: 900px) 100vw, 1200px" style={{ objectFit: 'cover', filter: 'saturate(1.05) contrast(1.05)' }} />
			</Box>

			{/* Lighter dark gradient from the text side (flipped for RTL by the style plugin): enough to read the text, while more of the photo shows */}
			<Box
				sx={{
					position: 'absolute',
					inset: 0,
					background: {
						xs: `linear-gradient(180deg, ${alpha(dark, 0.15)} 0%, ${alpha(dark, 0.78)} 60%)`,
						md: `linear-gradient(to right, ${alpha(dark, 0.82)} 0%, ${alpha(dark, 0.55)} 38%, ${alpha(dark, 0)} 65%)`
					}
				}}
			/>

			<Box
				sx={{
					position: 'relative',
					height: 1,
					minHeight: 'inherit',
					width: { xs: 1, md: '58%' },
					p: { xs: 3, md: 7 },
					display: 'flex',
					flexDirection: 'column',
					justifyContent: { xs: 'flex-end', md: 'center' }
				}}
			>
				<Box {...enter(active, 0.2, 0.5, { scale: '0.6' })}>
					<Box
						sx={{
							width: 56,
							height: 56,
							borderRadius: 3.5,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							bgcolor: 'primary.main',
							color: 'primary.contrastText',
							boxShadow: '0 12px 28px -8px rgba(0, 156, 156, 0.7)',
							'& .MuiSvgIcon-root': { fontSize: 28 }
						}}
					>
						<MenuItemIconAtom name={icon} />
					</Box>
				</Box>

				<Box {...enter(active, 0.3, 0.55, { translate: '0 16px' })}>
					<Typography
						component="h3"
						sx={{ mt: 3, fontSize: { xs: 24, md: 32 }, fontWeight: 700, lineHeight: 1.45, color: 'common.white', textShadow: '0 2px 14px rgba(0, 0, 0, 0.35)' }}
					>
						{title}
					</Typography>
				</Box>

				<Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, mt: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
					{items.map((item, index) => (
						<Box component="li" key={item} {...enter(active, 0.45 + index * 0.09, 0.45, { translate: '24px 0' })}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'flex-start',
									gap: 1.5,
									px: 2,
									py: 1.25,
									borderRadius: 3,
									bgcolor: alpha('#fff', 0.08),
									border: `1px solid ${alpha('#fff', 0.12)}`,
									backdropFilter: 'blur(6px)'
								}}
							>
								<Box
									sx={{
										mt: 0.25,
										flexShrink: 0,
										width: 22,
										height: 22,
										borderRadius: '50%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										bgcolor: 'tertiary.main',
										color: 'tertiary.contrastText',
										'& .MuiSvgIcon-root': { fontSize: 13 }
									}}
								>
									<CrossIconAtom />
								</Box>

								<Typography sx={{ fontSize: { xs: 14, md: 15 }, lineHeight: 1.8, color: alpha('#fff', 0.92) }}>{item}</Typography>
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	)
}

export default PainSlideMolecule
